import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LanguageService, TransportMode } from '@hazmatsuite-next/ui';
import { Observable, map } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverPackagingModel } from '../../../models/over-packaging.model';
import { EasyDgdService } from '../../../services/easydgd.service';
import { EnumPackageType } from '../../../models/package-type.enum';
import * as uuid from 'uuid';

@UntilDestroy()
@Component({
  selector: 'lib-easydgd-overpack-modal',
  templateUrl: './overpack-modal.component.html',
  styleUrls: ['./overpack-modal.component.scss'],
})
export class OverpackModalComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() isEdit = false;
  @Input() packageId = '';
  @Input() package?: OverPackagingModel;
  @Input() overpackItems: OverPackagingModel[] = [];
  @Input() overpackTitle = 'overpack';

  @Output() selectOverpack = new EventEmitter<string>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() closeOverpackModal = new EventEmitter<{ packageId: string, pkg: OverPackagingModel, isEdit: boolean }>();

  private model?: OverPackagingModel;

  public showOptions = false;
  public showForm = false;
  public formGroup = new FormGroup({
    amount: new FormControl(1, [Validators.required, Validators.min(1)]),
    packingType: new FormControl('', Validators.required),
    identification: new FormControl('', Validators.required)
  });

  public overpackSelectionFormGroup = new FormGroup({
    overpackId: new FormControl('', Validators.required)
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public packingListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['PackingListResource']));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public layoutResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['LayoutResource']));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public validationTranslations$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  constructor(
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService) { }


  ngOnInit(): void {
    this.amount?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((val: number | null) => {
      if (this.easyDgdService?.getTransportMode() === TransportMode.Air && +(val || 1) > 1) {
        this.identification?.setValidators(Validators.required);
        this.packingType?.setValidators(Validators.required);
      } else {
        this.identification?.clearValidators();
        this.packingType?.clearValidators();
      }
      this.identification?.updateValueAndValidity();
      this.packingType?.updateValueAndValidity();
    });

  }

  ngOnChanges(): void {
    this.showOptions = this.isEdit || this.overpackItems.length > 0;
    this.showForm = this.isEdit || !this.overpackItems.length;
    if (this.showForm) {
      this.model = this.package;
      this.amount?.setValue(this.package?.Amount || 1, { emitEvent: false });
      this.packingType?.setValue(this.package?.PackingType || '', { emitEvent: false });
      this.identification?.setValue(this.package?.Identification || '', { emitEvent: false });
    }
    this.overpackId?.setValue(this.package?.Id ?? '', { emitEvent: false });
  }

  get amount() {
    return this.formGroup?.get('amount');
  }
  get packingType() {
    return this.formGroup?.get('packingType');
  }
  get identification() {
    return this.formGroup?.get('identification');
  }
  get overpackId() {
    return this.overpackSelectionFormGroup.get('overpackId');
  }

  closeModal() {
    this.showForm = false;
    this.closeOverpackModal.emit();
  }

  createNew(): void {
    this.amount?.setValue(1, { emitEvent: false });
    this.packingType?.setValue('', { emitEvent: false });
    this.identification?.setValue('', { emitEvent: false });
    this.showForm = true;
  }

  select(): void {
    if (this.overpackSelectionFormGroup.valid) {
      return this.selectOverpack.next(this.overpackId?.value as string);
    }

    this.overpackSelectionFormGroup.markAllAsTouched();
  }

  save(isEdit: boolean): void {
    if (this.formGroup.valid) {
      this.closeOverpackModal.emit({
        packageId: this.packageId,
        pkg: {
          Id: isEdit ? this.model?.Id as string : uuid.v4(),
          Amount: this.amount?.value as number,
          PackingType: this.packingType?.value as string,
          Identification: this.identification?.value as string,
          Name: this.model?.Name ?? this.calculateName(),
          PackageType: EnumPackageType.Overpack
        } as OverPackagingModel,
        isEdit: isEdit
      });
      return this.formGroup.reset({ amount: 1 });
    }

    this.formGroup.markAllAsTouched();
  }

  private calculateName(): string {
    const packagingTypePartialString = this.packingType?.value ? `(${this.packingType.value})` : '';
    return `${this.amount?.value} ${this.overpackTitle}${packagingTypePartialString} ${this.identification?.value}`;
  }
}
